import { create } from 'zustand';

export interface PowerBISelection {
  type: 'report' | 'dashboard';
  name: string;
  webUrl: string;
  /** The workspace the item was picked FROM. Known with certainty on the modal path — the user
   *  just navigated into it — so the resolved helper text can read "Workspace / Report" exactly
   *  as it does for a pasted URL. Previously hardcoded null, which made a PICK show LESS than a
   *  paste. */
  workspaceName?: string | null;
}

interface PowerBIBrowseDialogContext {
  encryptedAccessToken?: string;
  integrationKeyId?: string;
  /** Base URL for the microsoft-bi smallapp microservice — sourced from `AppMeta.apiUrl`. */
  apiUrl: string;
  /** Resolves with the picked report/dashboard, or null on Cancel/close-without-selecting. */
  resolve: (selection: PowerBISelection | null) => void;
}

interface PowerBIBrowseDialogState {
  context: PowerBIBrowseDialogContext | null;
  isOpen: boolean;
  open: (context: PowerBIBrowseDialogContext) => void;
  close: (result: PowerBISelection | null) => void;
}

/**
 * Imperative open/close controller for the Power BI browse modal (content picker, R2).
 * Mirrors `moderation-dialog-store.ts`'s shape exactly (decision-of-record #4: one store, this
 * one — the picker never opens a nested picker from within itself, so Lane C's stack-based
 * `smallapp-picker-store.ts` pattern doesn't apply here).
 *
 * Caller side: the `openPowerBIBrowseModal` action handler wraps `open()` in a Promise the
 * same way `useModerationDialog` does — see plan step 14 (owned by another agent).
 */
export const usePowerBIBrowseDialogStore = create<PowerBIBrowseDialogState>((set, get) => ({
  context: null,
  isOpen: false,
  open: (context) => set({ context, isOpen: true }),
  close: (result) => {
    const ctx = get().context;
    ctx?.resolve(result);
    set({ context: null, isOpen: false });
  },
}));
